import { useState } from 'react';
import { Folder, Loader2, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useRole } from '@/hooks/useRole';
import { useDocuments } from './useDocuments';
import { DocumentUploadModal } from './DocumentUploadModal';
import { DocumentRow } from './DocumentRow';
import { DEFAULT_DOCUMENT_FILTERS, DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_OPTIONS } from './types';
import type { DocumentFilters, DocumentType } from './types';

export function DocumentsPage() {
  const { clientId, client } = useCurrentClient();
  const { isStaff } = useRole();
  const [filters, setFilters] = useState<DocumentFilters>(DEFAULT_DOCUMENT_FILTERS);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: docs = [], isLoading } = useDocuments(filters);

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">Documentos</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Folder className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para ver os documentos.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasActiveFilters =
    filters.search.trim() !== '' ||
    filters.type !== 'all' ||
    filters.fromDate !== '' ||
    filters.toDate !== '';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Documentos</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name ? `${client.name} · ` : ''}arquivos do cliente
          </p>
        </div>
        {isStaff && (
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} className="mr-2" />
            Enviar documento
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 lg:col-span-2">
              <Label htmlFor="doc-search" className="text-xs">
                Buscar
              </Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="doc-search"
                  placeholder="Nome, fornecedor, observações…"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <Select
                value={filters.type}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, type: v as DocumentType | 'all' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {DOCUMENT_TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {DOCUMENT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="doc-from" className="text-xs">
                  De
                </Label>
                <Input
                  id="doc-from"
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-to" className="text-xs">
                  Até
                </Label>
                <Input
                  id="doc-to"
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-xs text-muted-foreground">
                {docs.length} documento{docs.length === 1 ? '' : 's'} encontrado
                {docs.length === 1 ? '' : 's'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFilters(DEFAULT_DOCUMENT_FILTERS)}
                className="h-7 text-xs"
              >
                <X size={12} className="mr-1" />
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : docs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Folder className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {hasActiveFilters
                ? 'Nenhum documento corresponde aos filtros.'
                : 'Nenhum documento ainda.'}
            </p>
            {!hasActiveFilters && isStaff && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setUploadOpen(true)}
              >
                <Plus size={14} className="mr-1.5" />
                Enviar primeiro documento
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} readOnly={!isStaff} />
          ))}
        </div>
      )}

      <DocumentUploadModal open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
